/**
 * Step 3 — How the study was done (TESSA methodological template).
 */

export type MethodologyStep = {
  readonly id: string;
  readonly title: string;
  /** One line saying what the step produces, so the schema reads on its own. */
  readonly detail: string;
  /** Ink comes from the brand ramp in CSS (one shade deeper per step). */
  readonly icon: "site" | "services" | "problem" | "method" | "data" | "results";
};

export type TessaService = {
  readonly id: string;
  readonly label: string;
  readonly detail?: string;
  readonly icon:
    | "climate"
    | "water"
    | "wild"
    | "cultivated"
    | "recreation"
    | "cultural"
    | "pollination"
    | "coastal";
};

export const STUDY_HOW = {
  // Non-breaking hyphens (U+2011): at display sizes the title otherwise wraps
  // inside "a-t-elle".
  title: "Comment l’étude a‑t‑elle été réalisée ?",
  question: "Peut-on faire confiance aux résultats ?",
  lead: "Les résultats reposent sur la méthodologie TESSA, appliquée aux sites de Kounounkan et Madina-Oula pour évaluer rapidement les services écosystémiques à l’échelle du site.",
  trustNote:
    "La confiance vient de la triangulation : plusieurs types de preuves (terrain, cartes, enquêtes…) sont confrontés plutôt qu’une seule source isolée.",
  tessa: {
    acronym: "TESSA",
    fullName: "Toolkit for Ecosystem Service Site-based Assessment",
    definition:
      "TESSA est une boîte à outils pratique, conçue pour évaluer sur un site donné les services écosystémiques — c’est-à-dire les bénéfices que les personnes tirent de la nature. Elle guide l’identification des services importants, le choix des données et des méthodes de mesure, la comparaison d’états alternatifs du site (par exemple avant/après un changement d’usage), puis la communication des résultats pour éclairer les décisions.",
    whyItMatters:
      "Elle vise un équilibre entre simplicité et robustesse scientifique, pour être utilisable par des praticiens non spécialistes tout en produisant des informations crédibles pour la décision.",
    reference: {
      label: "BirdLife International — TESSA Tools",
      url: "https://www.birdlife.org/tessa-tools/",
      citation:
        "Peh, K. S.-H., Balmford, A. P., Bradbury, R. B., Brown, C., Butchart, S. H. M., Hughes, F. M. R., MacDonald, M. A., Stattersfield, A. J., Thomas, D. H. L., Trevelyan, R. J., Walpole, M. & Merriman, J. C. (2017). Toolkit for Ecosystem Service Site-based Assessment (TESSA). Version 2.0. Cambridge, UK.",
      doiUrl: "https://doi.org/10.1016/j.ecoser.2013.06.003",
    },
  },
  schemaIntro:
    "Outil développé pour évaluer rapidement les services écosystémiques à l’échelle des sites de Kounounkan et Madina-Oula.",
  schema: [
    {
      id: "1-sites",
      title: "Définition des sites et du contexte",
      detail: "Périmètre, limites et enjeux des paysages forestiers de Kounounkan et Madina-Oula.",
      icon: "site",
    },
    {
      id: "2-services",
      title: "Identification des services écosystémiques",
      detail: "Repérage des bénéfices que les populations tirent réellement du site.",
      icon: "services",
    },
    {
      id: "3-problem",
      title: "Définition de la problématique",
      detail: "Question de décision posée et états du site que l’on compare.",
      icon: "problem",
    },
    {
      id: "4-method",
      title: "Sélection de la méthode à employer",
      detail: "Protocoles retenus selon les moyens et les données disponibles.",
      icon: "method",
    },
    {
      id: "5-data",
      title: "Collecte des données",
      detail: "Terrain, enquêtes auprès des ménages, télédétection et sources existantes.",
      icon: "data",
    },
    {
      id: "6-results",
      title: "Analyse et communication des résultats",
      detail: "Triangulation des preuves, puis restitution pour éclairer la décision.",
      icon: "results",
    },
  ] satisfies readonly MethodologyStep[],
  services: [
    { id: "climate", label: "Régulation du climat", icon: "climate" },
    {
      id: "water",
      label: "Services liés à l’eau",
      detail: "approvisionnement, qualité, réduction des inondations",
      icon: "water",
    },
    { id: "wild", label: "Biens sauvages récoltés", icon: "wild" },
    { id: "cultivated", label: "Biens cultivés", icon: "cultivated" },
    { id: "recreation", label: "Loisirs basés sur la nature", icon: "recreation" },
    { id: "cultural", label: "Services culturels", icon: "cultural" },
    { id: "pollination", label: "Services de pollinisation", icon: "pollination" },
    { id: "coastal", label: "Protection côtière", icon: "coastal" },
  ] satisfies readonly TessaService[],
} as const;
